import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { WagmiProvider, useAccount, useSwitchChain } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RainbowKitProvider, getDefaultConfig, darkTheme } from '@rainbow-me/rainbowkit';
import { avalanche, mainnet, optimism, arbitrum, base, polygon, linea } from 'wagmi/chains';
import '@rainbow-me/rainbowkit/styles.css';
import Layout from './components/Layout';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Staking from './pages/Staking';
import Swap from './pages/Swap';
import Bridge from './pages/Bridge';
import Vaults from './pages/Vaults';
import VaultV3 from './pages/VaultV3';
import Admin from './pages/Admin';
import Signal from './pages/Signal';
import sonicIcon from './assets/chains/sonic.png';
import unichainIcon from './assets/chains/unichain.png';
import './styles/global.css';

// Define Sonic chain manually as it may not be in wagmi/chains yet
const sonic = {
  id: 146,
  name: 'Sonic',
  network: 'sonic',
  iconUrl: sonicIcon,
  nativeCurrency: {
    decimals: 18,
    name: 'Sonic',
    symbol: 'S',
  },
  rpcUrls: {
    default: { http: ['https://rpc.soniclabs.com'] },
    public: { http: ['https://rpc.soniclabs.com'] },
  },
  blockExplorers: {
    default: { name: 'Sonic Explorer', url: 'https://sonicscan.org' },
  },
};

// Define Unichain
const unichain = {
  id: 130,
  name: 'Unichain',
  network: 'unichain',
  iconUrl: unichainIcon,
  nativeCurrency: {
    decimals: 18,
    name: 'Ether',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: { http: ['https://lb.drpc.org/unichain/AllMzvTCtkaEj28yNral-Nsr3cKX_G4R75_AnqSgS7QB'] },
    public: { http: ['https://lb.drpc.org/unichain/AllMzvTCtkaEj28yNral-Nsr3cKX_G4R75_AnqSgS7QB'] },
  },
  blockExplorers: {
    default: { name: 'Unichain Explorer', url: 'https://unichain.blockscout.com/' },
  },
};

const config = getDefaultConfig({
  appName: 'Velvet AI',
  projectId: process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || 'YOUR_PROJECT_ID',
  chains: [avalanche, mainnet, optimism, arbitrum, base, polygon, linea, sonic, unichain],
  ssr: false,
});

const queryClient = new QueryClient();

// Custom avatar component - removing this as it's causing display issues
// We'll use the default avatar instead

// Component to handle automatic chain switching
function ChainSwitcher({ children }) {
  const { isConnected, chain } = useAccount();
  const { switchChain } = useSwitchChain();

  useEffect(() => {
    // Skip auto-switching on Bridge and Swap pages to allow cross-chain operations
    const isBridgePage = window.location.pathname === '/bridge';
    const isSwapPage = window.location.pathname === '/swap';

    // If connected and not on Avalanche (and not on bridge or swap page), switch to Avalanche
    if (isConnected && chain && chain.id !== avalanche.id && !isBridgePage && !isSwapPage) {
      switchChain({ chainId: avalanche.id });
    }
  }, [isConnected, chain, switchChain]);

  return <>{children}</>;
}

function App() {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider
          initialChain={avalanche}
          theme={darkTheme({
            accentColor: '#8b5cf6',
            accentColorForeground: 'white',
            borderRadius: 'large',
            fontStack: 'system',
            overlayBlur: 'small',
          })}
          appInfo={{
            appName: 'Velvet AI',
            learnMoreUrl: 'https://velvetai.io',
          }}
        >
          <ChainSwitcher>
            <Router>
              <Layout>
                <Routes>
                  <Route path='/' element={<Home />} />
                  <Route path='/signal' element={<Signal />} />
                  <Route path='/dashboard' element={<Dashboard />} />
                  <Route path='/staking' element={<Staking />} />
                  <Route path='/swap' element={<Swap />} />
                  <Route path='/bridge' element={<Bridge />} />
                  <Route path='/vaults' element={<Vaults />} />
                  <Route path='/vault' element={<VaultV3 />} />
                  <Route path='/admin' element={<Admin />} />
                </Routes>
              </Layout>
            </Router>
          </ChainSwitcher>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default App;
